import React, { useEffect, useRef, useState } from 'react';
import { ChevronRight, RefreshCw, Info, HelpCircle, GripVertical } from 'lucide-react';
import type { DraggableAttributes, DraggableSyntheticListeners } from '@dnd-kit/core';
import { TreeNode as TreeNodeType } from '../../models/TreeModel';
import type { DatabaseExplorerNode } from '../../models/nodeTypes';
import { TreeNodeIcon } from './TreeNodeIcon';
import { isColumnNode, isDatabaseObjectNode, isConnectionNode, ColumnTreeNode } from '../../models/DatabaseTreeModels';
import { isSchemaContainerFolderNode } from '../../models/nodeTypeGuards';
import { ColumnNodeBehaviors } from '../../models/NodeBehaviors';
import { getEngineForConnection } from '../../../connections/utils/connectionLookup';
import type { DatabaseEngineId } from '../../../../core/engines/engineTypes';
import { cn } from '../../../../lib/utils';
import { getUnverifiedDatabaseTooltip } from '../../../connection-diagnostics/utils/fallbackTooltip';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../../../components/ui/tooltip';

import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
import { useIsSchemaRefreshing } from '../../../intellisense/schemaRefresh/schemaRefreshStore';
import { PinButton } from './PinButton';
import { ConnectionNodeLabel } from './ConnectionNodeLabel';
import { PinnedWorkspaceRow } from './PinnedWorkspaceRow';
import { StorageNodeLabel, isStorageLabelNode } from './StorageNodeLabel';
import type { PinnedWorkspaceNode } from '../../models/nodeTypes';
import {
  handlePinnedWorkspaceRowDoubleClick,
  handlePinnedWorkspaceRowKeyDown,
  type PinnedWorkspaceOpenArgs,
} from './pinnedWorkspaceRowInteraction';
import { useJustPinnedStore } from '../../../database-objects/store/justPinnedStore';
/**
 * Sortable wiring passed from `SortablePinnedTreeNode` (a wrapper used only for
 * pinned siblings under a connection node). When present, the row container
 * forwards its ref to `setNodeRef` and applies the drag transform; the row
 * also renders a hover-only grip handle that carries `attributes` + `listeners`
 * so only the handle is a drag source — the rest of the row stays clickable
 * for normal expand/select.
 */
export interface TreeNodeSortableProps {
  setNodeRef: (node: HTMLElement | null) => void;
  style: React.CSSProperties;
  attributes: DraggableAttributes;
  listeners: DraggableSyntheticListeners;
  isDragging: boolean;
}

interface TreeNodeContentProps {
  node: TreeNodeType;
  depth: number;
  isSelected: boolean;
  isExpanded: boolean;
  showExpander: boolean;
  iconMap: Record<string, React.ElementType>;
  onNodeSelect: (e: React.MouseEvent) => void;
  onExpandToggle: (e: React.MouseEvent) => void;
  onContextMenu?: (e: React.MouseEvent) => void;
  isPreloading: boolean;
  isHighlighted?: boolean;
  shouldScrollIntoView?: boolean;
  onHighlightComplete?: () => void;
  onReconnect?: (connectionId: string) => Promise<boolean>;
  /**
   * Provided only for `pinned-*` rows that participate in the connection-
   * scoped `SortableContext`. When set, the row gets a grip drag-handle and
   * forwards its ref to dnd-kit so it can be measured.
   */
  sortable?: TreeNodeSortableProps;
  /** True when the row is rendered inside the tinted PinnedAreaShell. */
  insidePinnedArea?: boolean;
  /**
   * The owning connection's color (hex) when set. Propagated down from the
   * connection root so every descendant row renders a matching vertical bar
   * along its left edge — visually a continuous stripe through the subtree.
   */
  connectionColor?: string;
  /**
   * When true, the row element is marked as an HTML5 drag source. Only table
   * and view nodes set this; columns, folders, and other node types stay
   * non-draggable.
   */
  draggable?: boolean;
  /** Fires when the user begins dragging a draggable row. */
  onDragStart?: (e: React.DragEvent<HTMLDivElement>) => void;
  /**
   * When set, the row supports double-click AND Enter-key activation (S23 —
   * no such affordance exists elsewhere in the tree; see TreeNode.tsx). The
   * row becomes keyboard-focusable (`tabIndex=0`) only when this is provided,
   * so every other node type's behavior is unchanged.
   */
  onActivate?: () => void;
}

/**
 * A double-click opens a node; it must not also select a word of its label (release pass 2026-10-01: seen on Linux and
 * Windows, every engine — this is the shared tree). The second press of a multi-click is the one that selects a word, so
 * only it is prevented: one press and drag still selects text in the name (CH-25), and `dblclick` still fires.
 */
export function preventMultiClickTextSelection(event: { detail: number; preventDefault: () => void }): void {
  if (event.detail > 1) event.preventDefault();
}

export function TreeNodeContent({
  node,
  depth,
  isSelected,
  isExpanded,
  showExpander,
  iconMap,
  onNodeSelect,
  onExpandToggle,
  onContextMenu,
  isPreloading,
  isHighlighted,
  shouldScrollIntoView,
  onHighlightComplete,
  onReconnect,
  sortable,
  insidePinnedArea,
  connectionColor,
  draggable,
  onDragStart,
  onActivate,
}: TreeNodeContentProps) {
  const nodeRef = useRef<HTMLDivElement | null>(null);

  // A manual IntelliSense refresh (Ctrl+Shift+R, palette, this node's own
  // Refresh) for this database — shown in the same slot as the object
  // preload spinner. `false` for every other node type.
  const databaseMeta = node.type === 'database'
    ? (node.metadata as { connectionId?: string; name?: string } | undefined)
    : undefined;
  const isSchemaRefreshing = useIsSchemaRefreshing(databaseMeta?.connectionId, databaseMeta?.name);

  // Combined ref — own ref (for scrollIntoView on highlight) plus dnd-kit's
  // setNodeRef when this row is participating in a SortableContext.
  const setRowRef = (el: HTMLDivElement | null) => {
    nodeRef.current = el;
    sortable?.setNodeRef(el);
  };

  // Read the latest scroll-eligibility value at effect time without making it a
  // dependency — including it would reset the 600ms highlight timer whenever
  // another connection joins the burst.
  const shouldScrollIntoViewRef = useRef(shouldScrollIntoView);
  shouldScrollIntoViewRef.current = shouldScrollIntoView;

  // Scroll into view and trigger highlight animation when highlighted
  useEffect(() => {
    if (isHighlighted && nodeRef.current) {
      if (shouldScrollIntoViewRef.current) {
        nodeRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }

      // Remove highlight after animation completes (600ms)
      const timer = setTimeout(() => {
        onHighlightComplete?.();
      }, 600);

      return () => clearTimeout(timer);
    }
  }, [isHighlighted, onHighlightComplete]);

  const baseIndent = 12;
  const minIndent = 22;
  const indentation = depth === 0 ? minIndent : minIndent + depth * baseIndent;

  // Task 12 — Pin-resolution-driven styling. Pinned wrapper nodes
  // (`pinned-database` / `pinned-folder` / `pinned-object`) carry a `resolution`
  // field stamped by `buildPinnedChildren` from the TanStack Query cache. All
  // other rows render as `'resolved'` so the row chrome stays untouched.
  const pinnedResolution: 'resolved' | 'unresolved' | 'stale' =
    node.type === 'pinned-database' ||
    node.type === 'pinned-schema' ||
    node.type === 'pinned-folder' ||
    node.type === 'pinned-object' ||
    // Azure Storage pins carry the same `resolution` stamp (resolved from the
    // `['azure-storage', …]` listing cache); without this arm the row rendered
    // `resolved` forever and a removed container never read as stale.
    node.type === 'pinned-storage'
      ? (node as unknown as { resolution: 'resolved' | 'unresolved' | 'stale' }).resolution
      : 'resolved';

  const resolutionClass =
    pinnedResolution === 'stale'
      ? 'opacity-50 italic'
      : pinnedResolution === 'unresolved'
        ? 'opacity-70'
        : '';

  // Rows inside the tinted PinnedAreaShell keep a transparent unselected
  // background so the shell tint shows through the whole area (instead of an
  // opaque `bg-background` covering it) — including expanded descendants.
  const unselectedRowBg = insidePinnedArea ? 'bg-transparent hover:bg-muted' : 'bg-background hover:bg-muted';

  // Guard expansion ONLY when the pin is known-stale (the underlying object
  // is gone from the live cache). Review finding #13 — the previous guard
  // also blocked the 'unresolved' state, which is the transient cold-boot
  // state BEFORE the cache subscriber has classified the pin. That stranded
  // cold-boot users staring at click-dead rows for the 1-3s before the first
  // `database-objects` query landed. After the #14 + #10 fixes, 'unresolved'
  // is the legitimate "loading" state — clicks must flow through (they'll
  // no-op via showExpander=false on pinned wrappers regardless, but the
  // row selection still needs to land).
  const guardedExpandToggle = (e: React.MouseEvent) => {
    if (pinnedResolution === 'stale') return;
    onExpandToggle(e);
  };

  const staleTooltip =
    pinnedResolution === 'stale' ? 'No longer present in the database' : undefined;

  // Task 17 (F14/G17) — interaction additions are scoped STRICTLY to
  // `pinned-workspace` rows: every other node type must keep today's exact
  // DOM (no tabIndex, no new handlers). `pinnedWorkspaceOpenArgs` is `null`
  // for every other node type, and every prop below that's derived from it
  // collapses to `undefined` (an omitted DOM attribute) in that case.
  const pinnedWorkspaceNode = node.type === 'pinned-workspace' ? (node as unknown as PinnedWorkspaceNode) : null;
  const pinnedWorkspaceOpenArgs: PinnedWorkspaceOpenArgs | null = pinnedWorkspaceNode
    ? { connectionId: pinnedWorkspaceNode.metadata.connectionId, ref: pinnedWorkspaceNode.pinRef }
    : null;
  const pinnedWorkspacePinId = pinnedWorkspaceNode?.pinRef.id ?? null;

  // Flash-on-create (F14/G17) — Task 18 (Wave E) calls
  // `useJustPinnedStore.getState().set(newPinId)` right after a new workspace
  // pin is created. The selector below returns a plain boolean scoped to
  // THIS row's own pin id, so a flash targeting one row never re-renders any
  // other row in this hot tree component (`s.pinId === otherRow'sId` stays
  // `false` for every unrelated row regardless of which pin is flashing).
  const isJustPinnedTarget = useJustPinnedStore(
    (s) => pinnedWorkspacePinId !== null && s.pinId === pinnedWorkspacePinId,
  );
  const [isJustPinnedFlash, setIsJustPinnedFlash] = useState(false);

  useEffect(() => {
    if (!isJustPinnedTarget) {
      // F1 fix — a row that is no longer the flash target (because a later
      // pin retargeted the store to a different row) must drop its own flash
      // state immediately instead of staying stuck until it remounts.
      setIsJustPinnedFlash(false);
      return;
    }
    setIsJustPinnedFlash(true);
    nodeRef.current?.scrollIntoView({ block: 'nearest' });
    const timer = setTimeout(() => {
      setIsJustPinnedFlash(false);
      // Consume the signal so a later re-render of this same row (or a
      // stray remount) never re-triggers the flash from a stale store value.
      useJustPinnedStore.getState().clear();
    }, 1500);
    return () => clearTimeout(timer);
  }, [isJustPinnedTarget]);

  return (
    <div
      ref={setRowRef}
      className={cn(
        `
        group flex items-center px-2 py-1 text-sm [outline:none] [border:none]
        ${isSelected ? 'bg-accent text-accent-foreground !important' : unselectedRowBg}
        ${isHighlighted ? 'animate-highlight-pulse' : ''}
        cursor-pointer relative select-none
      `,
        // Flash-on-create (F14/G17) — a subtle indigo ring matching the tint
        // already used by `PinnedAreaShell`, pulsing twice. Never combined
        // with `[box-shadow:none]` in the same className string — only one
        // of the two is ever present, so there's no cascade-order ambiguity
        // between the arbitrary property and the `ring-*` utility.
        isJustPinnedFlash
          ? 'ring-2 ring-inset ring-indigo-500/40 dark:ring-indigo-400/40 animate-[pulse_0.75s_ease-in-out_2]'
          : '[box-shadow:none]',
        resolutionClass,
        sortable?.isDragging ? 'opacity-50' : '',
      )}
      data-testid={`tree-node-${node.id}`}
      data-tree-node-row=""
      data-selected={isSelected ? 'true' : 'false'}
      data-node-id={node.id}
      data-node-type={node.type}
      data-node-expanded={isExpanded ? 'true' : 'false'}
      data-pin-resolution={pinnedResolution}
      data-just-pinned={isJustPinnedFlash ? 'true' : undefined}
      style={{ paddingLeft: `${indentation}px`, ...(sortable?.style ?? {}) }}
      draggable={draggable}
      onDragStart={onDragStart}
      onClick={onNodeSelect}
      onMouseDown={preventMultiClickTextSelection}
      onContextMenu={onContextMenu}
      tabIndex={onActivate || pinnedWorkspaceOpenArgs ? 0 : undefined}
      onDoubleClick={
        pinnedWorkspaceOpenArgs
          ? (e) => handlePinnedWorkspaceRowDoubleClick(e, pinnedWorkspaceOpenArgs)
          : onActivate
            ? (e) => {
                e.stopPropagation();
                onActivate();
              }
            : undefined
      }
      onKeyDown={
        pinnedWorkspaceOpenArgs
          ? (e) => handlePinnedWorkspaceRowKeyDown(e, pinnedWorkspaceOpenArgs)
          : onActivate
            ? (e) => {
                if (e.key === 'Enter') {
                  e.stopPropagation();
                  onActivate();
                }
              }
            : undefined
      }
    >
      {connectionColor && (
        <div
          aria-hidden
          data-testid="connection-color-bar"
          className="pointer-events-none absolute left-0 top-0 bottom-0 w-[3px]"
          style={{ backgroundColor: connectionColor }}
        />
      )}
      {sortable && (
        <button
          type="button"
          {...sortable.attributes}
          {...sortable.listeners}
          onClick={(e) => e.stopPropagation()}
          // 2026-05-26 — `z-20` is load-bearing. Without it, the indent-block
          // div rendered AFTER this grip (in DOM order) paints on top because
          // both are absolute-positioned at `left-0` with no z-index, and the
          // later sibling wins the stack. That swallowed pointerdown on the
          // grip and broke drag-to-reorder entirely. The chevron-overlay
          // inside the indent block stays at default z, so chevron clicks
          // still land on it — only the leftmost ~18px (where the grip lives)
          // is reserved for drag pointer events.
          className="absolute z-20 left-0 top-1/2 -translate-y-1/2 ml-0.5 p-0.5 rounded text-muted-foreground hover:text-foreground hover:bg-muted opacity-0 group-hover:opacity-100 focus-visible:opacity-100 cursor-grab active:cursor-grabbing"
          aria-label="Reorder pin"
          title="Drag to reorder"
          data-testid="pinned-row-drag-handle"
        >
          <GripVertical className="h-3.5 w-3.5" />
        </button>
      )}
      <div
        className="absolute left-0 flex items-center h-full"
        style={{ width: `${indentation}px` }}
      >
        {showExpander && (
          <div
            className="absolute inset-0 cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              guardedExpandToggle(e);
            }}
          />
        )}

        {showExpander && (
          <div
            className="absolute inset-y-0 right-0 flex items-center pr-1"
            onClick={(e) => {
              e.stopPropagation();
              guardedExpandToggle(e);
            }}
          >
            <ChevronRight
              className={cn(
                'h-4 w-4 opacity-70 transition-transform duration-150',
                isExpanded && 'rotate-90',
              )}
            />
          </div>
        )}
      </div>

      {/* CH-25 — the row above is `select-none` (chevron/indent chrome, the
          drag handle, the trailing pin button); this wrapper opts the name
          itself back into selection. `user-select` is inherited, so a plain
          child re-declaring `select-text` is what actually restores it —
          removing `select-none` from an ancestor is not the same thing.

          Fix round 1 (Minor #4): this wrapper's scope is the whole node-label
          cluster, not just `node.name` — for connection nodes that also
          covers the engine short-label and the "Disconnected" text (both
          rendered in the same flex span below), and for other node types it
          covers column-type annotations, schema prefixes, and directory
          paths. That's intentional, not an oversight: every one of those
          branches renders a DIFFERENT shape of JSX (see the ternary below),
          so narrowing to "just the name" would mean threading a `select-none`
          island back into each branch individually — and every other text in
          that same row is itself an identifier/value a user would plausibly
          want to copy (a directory path, a schema-qualified name, a column's
          type), i.e. exactly what CH-25 says must stay selectable. The only
          genuinely chrome-only elements in this row (chevron, drag handle,
          pin button) already live in the separate select-none div above;
          the reconnect button a few lines down stays clickable regardless
          (button labels are unaffected by an ancestor's user-select). */}
      <div className="flex items-center gap-2 truncate select-text" title={staleTooltip}>
        {node.type === 'pinned-workspace' ? (
          // Task 16 — a workspace pin's icon depends on its per-instance
          // `workspaceKind`, not the static `node.type`, so it owns its own
          // icon + label + tooltip instead of going through `TreeNodeIcon` +
          // the name ternary below (see `PinnedWorkspaceRow` doc comment).
          <PinnedWorkspaceRow node={node as unknown as PinnedWorkspaceNode} />
        ) : (
          <>
        <div
          className="cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            guardedExpandToggle(e);
          }}
        >
          <TreeNodeIcon node={node} isExpanded={isExpanded} iconMap={iconMap} />
        </div>

        {isStorageLabelNode(node) ? (
          <StorageNodeLabel node={node} />
        ) : isColumnNode(node) ? (
          <span className="truncate">
            {node.name}
            <span className="text-xs text-muted-foreground ml-1">
              ({node.metadata.isPrimaryKey ? 'PK, ' : ''}
              {ColumnNodeBehaviors.getFormattedType(
                node as ColumnTreeNode,
                // F1 (Soporte round 7) — SQL Server reports a column's length
                // in BYTES, so the label needs the owning connection's engine
                // to print `nvarchar(250)` instead of `nvarchar(500)`.
                getEngineForConnection(
                  (node as ColumnTreeNode).metadata.connectionId
                ) as DatabaseEngineId
              )}
              {node.metadata.isNullable ? ', null' : ', not null'})
            </span>
          </span>
        ) : node.type === 'directory' && isDatabaseObjectNode(node) ? (
          <span className="truncate" title={node.metadata?.objectDetails?.metadata?.directory_path as string}>
            {node.name}
            {node.metadata?.objectDetails?.metadata?.directory_path && (
              <span className="text-xs text-muted-foreground ml-1">
                ({node.metadata.objectDetails.metadata.directory_path as string})
              </span>
            )}
          </span>
        ) : isDatabaseObjectNode(node) && node.metadata?.objectDetails?.schema ? (
          (() => {
            const parentHasSchema =
              !!node.parentId &&
              /^(tables-folder|views-folder|stored-procedures-folder|functions-folder)<->[^<]+<->[^<]+<->[^<]+$/.test(
                node.parentId,
              );
            if (parentHasSchema) {
              return <span className="truncate">{node.name}</span>;
            }
            return (
              <span className="truncate">
                <span className="text-muted-foreground">
                  {node.metadata.objectDetails.schema}.
                </span>
                {node.name}
              </span>
            );
          })()
        ) : isConnectionNode(node) ? (
          <ConnectionNodeLabel node={node} onReconnect={onReconnect} />
        ) : node.type === 'database' ? (
          <span className="truncate flex items-center gap-1.5">
            {node.name}
            {(node.metadata as { unverified?: 'config' | 'known' })?.unverified ? (
              <UnverifiedTreeIndicator
                kind={(node.metadata as { unverified: 'config' | 'known' }).unverified}
              />
            ) : null}
            {node.metadata?.isPreloadingObjects ? (
              <RandomDotmSpinner size={11} className="text-muted-foreground flex-shrink-0 opacity-90" />
            ) : isSchemaRefreshing ? (
              <span
                className="inline-flex flex-shrink-0"
                data-testid="explorer-intellisense-refreshing"
                title="Refreshing IntelliSense…"
              >
                <RandomDotmSpinner size={11} className="text-muted-foreground opacity-90" />
              </span>
            ) : null}
          </span>
        ) : isSchemaContainerFolderNode(node) ? (
          <span className="truncate">
            {node.name}
            <span className="text-muted-foreground/60 text-xs ml-1.5 font-normal">
              ({node.metadata.objectCount})
            </span>
          </span>
        ) : (node.metadata as { action?: string })?.action === 'show-connection-diagnostics' ? (
          <span
            className="inline-flex h-6 items-center gap-1 rounded-md border border-input bg-background px-2 text-xs font-medium shadow-xs hover:bg-accent hover:text-accent-foreground"
            data-testid={`tree-node-show-details-${node.id}-action`}
          >
            <Info className="h-3 w-3" aria-hidden />
            <span className="truncate">{node.name}</span>
          </span>
        ) : (node.metadata as { action?: string })?.action === 'retry-list-databases' ? (
          <span
            className="inline-flex h-6 items-center gap-1 rounded-md border border-input bg-background px-2 text-xs font-medium shadow-xs hover:bg-accent hover:text-accent-foreground"
            data-testid={`tree-node-retry-${node.id}-action`}
          >
            <RefreshCw className="h-3 w-3" aria-hidden />
            <span className="truncate">{node.name}</span>
          </span>
        ) : (
          <span className="truncate">{node.name}</span>
        )}
          </>
        )}

        {isPreloading && node.metadata?.loadingStage && (
          <span className="text-xs text-muted-foreground ml-2 tabular-nums">
            {node.metadata.loadingStage} -{' '}
            {node.metadata.loadingProgress !== undefined
              ? `${node.metadata.loadingProgress}%`
              : 'Loading...'}
          </span>
        )}
      </div>

      <PinButton node={node as unknown as DatabaseExplorerNode} />
    </div>
  );
}

/**
 * Slice 5 — `?` indicator on database tree rows that came from the smart-
 * fallback list (config.database / knownDatabases) when listing failed.
 * Tooltip wording is centralised in `fallbackTooltip.ts`.
 */
function UnverifiedTreeIndicator({ kind }: { kind: 'config' | 'known' }) {
  const tooltip = getUnverifiedDatabaseTooltip(kind);
  return (
    <TooltipProvider>
      <Tooltip delayDuration={150}>
        <TooltipTrigger asChild>
          <span
            className="inline-flex items-center text-muted-foreground/70 cursor-help"
            data-testid={`tree-unverified-db-indicator-${kind}`}
            data-fallback-kind={kind}
            aria-label="Presence not verified"
          >
            <HelpCircle className="h-3 w-3" aria-hidden />
          </span>
        </TooltipTrigger>
        <TooltipContent side="right" className="max-w-xs text-xs">
          {tooltip}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

